// File: frontend/src/components/DiamondModel.tsx
// Diamond Model visualization for threat intelligence articles.
// Maps STIX 2.1 entities to the 4 vertices: Adversary, Capability, Infrastructure, Victim.

import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import {
    Box,
    CircularProgress,
    Alert,
    Typography,
    Paper,
    Chip,
    Collapse,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DiamondModelData, DiamondVertex, DiamondItem } from '../types/diamondModel';
import { cyber } from '../theme';
import { STIXBundle, STIXObject } from '../types/stix';

interface DiamondModelProps {
    stixUrl?: string;
}

// Colors for each vertex of the Diamond Model
const VERTEX_COLORS = {
    adversary: { main: '#FF6B6B', bg: 'rgba(255, 107, 107, 0.12)', border: 'rgba(255, 107, 107, 0.4)' },
    capability: { main: '#FFB347', bg: 'rgba(255, 179, 71, 0.12)', border: 'rgba(255, 179, 71, 0.4)' },
    infrastructure: { main: '#74B9FF', bg: 'rgba(116, 185, 255, 0.12)', border: 'rgba(116, 185, 255, 0.4)' },
    victim: { main: '#6BCF7F', bg: 'rgba(107, 207, 127, 0.12)', border: 'rgba(107, 207, 127, 0.4)' },
};

/**
 * Extract indicator IOC type from STIX pattern or name.
 * Returns 'domain', 'ip', 'url', 'hash', or 'other'.
 */
function getIndicatorIocType(obj: STIXObject): string {
    const pattern = obj.pattern || '';
    const name = (obj.name || '').toUpperCase();
    if (pattern.includes('domain-name') || name.startsWith('DOMAIN')) return 'domain';
    if (pattern.includes('ipv4-addr') || pattern.includes('ipv6-addr') || name.startsWith('IP')) return 'ip';
    if (pattern.includes('url') || name.startsWith('URL')) return 'url';
    return 'other';
}

/**
 * Extract the actual IOC value from a STIX indicator's pattern field.
 * STIX patterns look like: [ipv4-addr:value = '185.220.101.50']
 * Returns the extracted value, or null if parsing fails.
 */
function extractIocValueFromPattern(pattern: string): string | null {
    // Match quoted value inside STIX pattern: ... = 'value' or ... = "value"
    const match = pattern.match(/=\s*'([^']+)'/) || pattern.match(/=\s*"([^"]+)"/);
    return match ? match[1] : null;
}

/**
 * Check if a STIX identity object is a report publisher/author/platform identity
 * that should NOT be classified as a Victim in the Diamond Model.
 *
 * The STIX bundles generated by this platform always include identity objects for:
 * - "TI Mindmap HUB" (the platform itself)
 * - The publisher/source of the report
 * - The author of the threat intelligence report
 * - "manual_submission" (for manually submitted articles)
 * These are metadata identities, not attack victims.
 */
function isReportPublisherOrPlatform(obj: STIXObject): boolean {
    const name = (obj.name || '').toLowerCase();
    const desc = (obj.description || '').toLowerCase();

    // Platform identity
    if (name.includes('ti mindmap') || name.includes('mindmap hub')) return true;

    // Manual submission placeholder
    if (name === 'manual_submission') return true;

    // Description indicates publisher/author role rather than victim
    if (desc.includes('publisher of this threat intelligence report')) return true;
    if (desc.includes('author of this threat intelligence report')) return true;
    if (desc.includes('is the publisher of')) return true;
    if (desc.includes('is the author of')) return true;

    // identity_class "system" is typically the platform itself
    if (obj.identity_class === 'system') return true;

    return false;
}

/**
 * Parse a STIX 2.1 bundle and extract Diamond Model data.
 * Maps STIX object types to Diamond Model vertices:
 * - threat-actor → Adversary
 * - malware, tool, attack-pattern → Capability
 * - infrastructure, indicator (domain/ip/url) → Infrastructure
 * - identity (victim), location → Victim
 */
function parseStixToDiamondModel(bundle: STIXBundle): DiamondModelData {
    const adversaryItems: DiamondItem[] = [];
    const capabilityItems: DiamondItem[] = [];
    const infrastructureItems: DiamondItem[] = [];
    const victimItems: DiamondItem[] = [];

    for (const obj of bundle.objects) {
        switch (obj.type) {
            case 'threat-actor':
                adversaryItems.push({
                    id: obj.id,
                    name: obj.name || 'Unknown Threat Actor',
                    description: obj.description,
                    type: obj.type,
                    aliases: obj.aliases,
                });
                break;

            case 'malware':
                capabilityItems.push({
                    id: obj.id,
                    name: obj.name || 'Unknown Malware',
                    description: obj.description,
                    type: obj.type,
                    aliases: obj.aliases,
                });
                break;

            case 'tool':
                capabilityItems.push({
                    id: obj.id,
                    name: obj.name || 'Unknown Tool',
                    description: obj.description,
                    type: obj.type,
                });
                break;

            case 'attack-pattern':
                capabilityItems.push({
                    id: obj.id,
                    name: obj.name || 'Unknown Technique',
                    description: obj.description,
                    type: obj.type,
                });
                break;

            case 'infrastructure':
                infrastructureItems.push({
                    id: obj.id,
                    name: obj.name || 'Unknown Infrastructure',
                    description: obj.description,
                    type: obj.type,
                });
                break;

            case 'indicator': {
                const iocType = getIndicatorIocType(obj);
                if (iocType === 'domain' || iocType === 'ip' || iocType === 'url') {
                    // Extract the actual IOC value from the STIX pattern to display it prominently
                    const iocValue = extractIocValueFromPattern(obj.pattern || '');
                    const displayName = iocValue || obj.name || 'Unknown Indicator';
                    infrastructureItems.push({
                        id: obj.id,
                        name: displayName,
                        description: obj.description,
                        type: `indicator-${iocType}`,
                    });
                }
                break;
            }

            // SCO types that indicate infrastructure
            case 'ipv4-addr':
            case 'ipv6-addr':
            case 'domain-name':
                infrastructureItems.push({
                    id: obj.id,
                    name: obj.value || obj.name || obj.type,
                    description: undefined,
                    type: obj.type,
                });
                break;

            case 'identity':
                // Exclude platform identities, report publishers, authors, and submission placeholders.
                // These are metadata about who produced the report, not victims of the attack.
                if (!isReportPublisherOrPlatform(obj)) {
                    victimItems.push({
                        id: obj.id,
                        name: obj.name || 'Unknown Identity',
                        description: obj.description,
                        type: obj.type,
                    });
                }
                break;

            case 'location':
                victimItems.push({
                    id: obj.id,
                    name: obj.name || obj.country || 'Unknown Location',
                    description: obj.description,
                    type: obj.type,
                });
                break;
        }
    }

    return {
        adversary: { label: 'Adversary', items: adversaryItems },
        capability: { label: 'Capability', items: capabilityItems },
        infrastructure: { label: 'Infrastructure', items: infrastructureItems },
        victim: { label: 'Victim', items: victimItems },
    };
}

/** A single vertex card in the Diamond Model */
const VertexCard: React.FC<{
    vertex: DiamondVertex;
    colorKey: keyof typeof VERTEX_COLORS;
    expanded: boolean;
    onToggle: () => void;
}> = ({ vertex, colorKey, expanded, onToggle }) => {
    const colors = VERTEX_COLORS[colorKey];
    const hasItems = vertex.items.length > 0;

    return (
        <Paper
            onClick={hasItems ? onToggle : undefined}
            sx={{
                p: 2,
                cursor: hasItems ? 'pointer' : 'default',
                bgcolor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': hasItems ? {
                    borderColor: colors.main,
                    boxShadow: `0 0 12px ${colors.border}`,
                } : {},
                width: '100%',
                maxWidth: 340,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: colors.main,
                        flexShrink: 0,
                    }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.main }}>
                        {vertex.label}
                    </Typography>
                    <Chip
                        label={vertex.items.length}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: colors.border,
                            color: '#fff',
                        }}
                    />
                </Box>
                {hasItems && (
                    <IconButton size="small" sx={{ color: colors.main }}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
            </Box>

            {!hasItems && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    No data available
                </Typography>
            )}

            <Collapse in={expanded} timeout={300}>
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {vertex.items.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,0,0,0.2)',
                                borderLeft: `3px solid ${colors.main}`,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={
                                    // IOC values (indicators / network observables) render in mono cyan
                                    /^(indicator-|ipv4-addr|ipv6-addr|domain-name)/.test(item.type)
                                        ? { fontWeight: 500, fontFamily: cyber.fontMono, fontSize: '0.78rem', color: 'rgba(77, 208, 225, 0.9)', wordBreak: 'break-all' }
                                        : { fontWeight: 500 }
                                }
                            >
                                {item.name}
                            </Typography>
                            {item.aliases && item.aliases.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {item.aliases.map((alias) => (
                                        <Chip
                                            key={alias}
                                            label={alias}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: '0.65rem', borderColor: colors.border, color: 'text.secondary' }}
                                        />
                                    ))}
                                </Box>
                            )}
                            {item.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {item.description.length > 120 ? `${item.description.substring(0, 120)}...` : item.description}
                                </Typography>
                            )}
                            <Chip
                                label={item.type}
                                size="small"
                                sx={{ mt: 0.5, height: 16, fontSize: '0.6rem', fontFamily: cyber.fontMono, bgcolor: 'rgba(255,255,255,0.05)' }}
                            />
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
};

/** SVG dashed connection lines between vertices */
const DiamondLines: React.FC = () => (
    <svg
        viewBox="0 0 400 400"
        style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
        }}
        preserveAspectRatio="xMidYMid meet"
    >
        {/* Adversary (top) to Capability (left) */}
        <line x1="200" y1="60" x2="60" y2="200" stroke={cyber.border} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7" />
        {/* Adversary (top) to Infrastructure (right) */}
        <line x1="200" y1="60" x2="340" y2="200" stroke={cyber.border} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7" />
        {/* Capability (left) to Victim (bottom) */}
        <line x1="60" y1="200" x2="200" y2="340" stroke={cyber.border} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7" />
        {/* Infrastructure (right) to Victim (bottom) */}
        <line x1="340" y1="200" x2="200" y2="340" stroke={cyber.border} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7" />
        {/* Capability (left) to Infrastructure (right) — horizontal */}
        <line x1="60" y1="200" x2="340" y2="200" stroke={cyber.border} strokeWidth="1" strokeDasharray="4,4" opacity="0.45" />
        {/* Adversary (top) to Victim (bottom) — vertical */}
        <line x1="200" y1="60" x2="200" y2="340" stroke={cyber.border} strokeWidth="1" strokeDasharray="4,4" opacity="0.45" />
    </svg>
);

const DiamondModel: React.FC<DiamondModelProps> = ({ stixUrl }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { simpleTextFetcher } = useApi();
    const [expandedVertex, setExpandedVertex] = useState<string | null>(null);

    const { data: stixJsonString, error, isLoading } = useSWR(stixUrl, simpleTextFetcher);

    const diamondData = useMemo<DiamondModelData | null>(() => {
        if (!stixJsonString) return null;
        try {
            const bundle: STIXBundle = JSON.parse(stixJsonString);
            return parseStixToDiamondModel(bundle);
        } catch {
            return null;
        }
    }, [stixJsonString]);

    const handleToggle = (vertex: string) => {
        setExpandedVertex(prev => prev === vertex ? null : vertex);
    };

    if (!stixUrl) {
        return <Alert severity="info">No STIX bundle available to generate Diamond Model.</Alert>;
    }
    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error">Could not load STIX data for Diamond Model.</Alert>;
    }
    if (!diamondData) {
        return <Alert severity="warning">Could not parse STIX data for Diamond Model.</Alert>;
    }

    // Mobile layout: stack vertices vertically with detail panels below
    if (isMobile) {
        return (
            <Box sx={{ p: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Diamond Model
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    <VertexCard vertex={diamondData.adversary} colorKey="adversary" expanded={expandedVertex === 'adversary'} onToggle={() => handleToggle('adversary')} />
                    <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <VertexCard vertex={diamondData.capability} colorKey="capability" expanded={expandedVertex === 'capability'} onToggle={() => handleToggle('capability')} />
                        <VertexCard vertex={diamondData.infrastructure} colorKey="infrastructure" expanded={expandedVertex === 'infrastructure'} onToggle={() => handleToggle('infrastructure')} />
                    </Box>
                    <VertexCard vertex={diamondData.victim} colorKey="victim" expanded={expandedVertex === 'victim'} onToggle={() => handleToggle('victim')} />
                </Box>
            </Box>
        );
    }

    // Desktop layout: diamond shape with SVG connection lines
    return (
        <Box sx={{ p: { sm: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Diamond Model
            </Typography>

            <Box sx={{ position: 'relative', maxWidth: 800, mx: 'auto' }}>
                {/* SVG connection lines */}
                <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', maxHeight: 600 }}>
                    <Box sx={{ position: 'absolute', inset: 0 }}>
                        <DiamondLines />

                        {/* Adversary — Top */}
                        <Box sx={{
                            position: 'absolute',
                            top: '2%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <VertexCard vertex={diamondData.adversary} colorKey="adversary" expanded={expandedVertex === 'adversary'} onToggle={() => handleToggle('adversary')} />
                        </Box>

                        {/* Capability — Left */}
                        <Box sx={{
                            position: 'absolute',
                            top: '38%',
                            left: '1%',
                            zIndex: 1,
                        }}>
                            <VertexCard vertex={diamondData.capability} colorKey="capability" expanded={expandedVertex === 'capability'} onToggle={() => handleToggle('capability')} />
                        </Box>

                        {/* Infrastructure — Right */}
                        <Box sx={{
                            position: 'absolute',
                            top: '38%',
                            right: '1%',
                            zIndex: 1,
                        }}>
                            <VertexCard vertex={diamondData.infrastructure} colorKey="infrastructure" expanded={expandedVertex === 'infrastructure'} onToggle={() => handleToggle('infrastructure')} />
                        </Box>

                        {/* Victim — Bottom */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: '2%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <VertexCard vertex={diamondData.victim} colorKey="victim" expanded={expandedVertex === 'victim'} onToggle={() => handleToggle('victim')} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default DiamondModel;
